// web/src/components/WelcomeWalkthrough.tsx
// Three-screen full-screen modal shown once on the first dashboard visit. It
// explains what the app actually does before the user has to engage with the
// UI. After completion (or "Skip"), we PATCH `welcomeSeen: true` and the
// server stamps welcomeSeenAt — so it doesn't re-appear on a second device.
//
// Distinct from OnboardingCard: that's an inline reminder that lives on the
// dashboard until the parent records their first match. This modal is a
// one-shot magazine-style intro. Both can coexist (modal first, card after).
//
// UX deliberately minimal — no animations beyond a fade between screens. The
// user is brand new; they want to get to the app, not watch a tutorial. Three
// screens is the ceiling, and the "Skip" link is always visible.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './Button';
import { useParentApi } from '../api/parent';
import type { Parent } from '../types';

interface Props {
  parent: Parent;
  /** Called once the server has confirmed the dismissal. */
  onDismissed: () => void;
}

interface Slide {
  eyebrow: string;
  title: string;
  body: string;
  /** Optional CTA label/href for the final slide that jumps straight into /kids/new. */
  cta?: { label: string; to?: string };
}

const SLIDES: Slide[] = [
  {
    eyebrow: 'Issue 1 · Welcome',
    title: 'Your kid’s season, kept like a magazine.',
    body:
      'MyFootballJournal turns every grassroots match into a covered, captioned page. Goals, assists, MoM, captain runs — you tap it once, we keep it forever.',
  },
  {
    eyebrow: 'Issue 2 · Achievements',
    title: '217 milestones, unlocking themselves.',
    body:
      'First goal. Hat-trick. Ten clean sheets. We watch the data and award the badge — you don’t have to remember a thing. They build a trophy cabinet without trying.',
  },
  {
    eyebrow: 'Issue 3 · Get started',
    title: 'Add your player. The journal starts there.',
    body:
      'Name, date of birth, kit colour. Two minutes. After that, the next match you record writes your first cover story.',
    cta: { label: 'Add your child', to: '/kids/new' },
  },
];

export function WelcomeWalkthrough({ parent, onDismissed }: Props) {
  const api = useParentApi();
  const [index, setIndex] = useState(0);
  const [closing, setClosing] = useState(false);

  if (parent.welcomeSeenAt) return null;

  const slide = SLIDES[index];
  // If for any reason index falls out of range we render nothing rather than
  // crashing — the user can just navigate the dashboard underneath.
  if (!slide) return null;

  const isLast = index === SLIDES.length - 1;

  const finish = async (followCtaTo?: string) => {
    // Optimistically dismiss so we don't block the user on a slow network. If
    // the PATCH fails we'll just show the modal again next time — best-effort.
    setClosing(true);
    onDismissed();
    try {
      await api.updateMe({ welcomeSeen: true });
    } catch {
      // Tolerate failure — the dismissal sticks for this session, and a
      // retried dashboard visit re-shows the modal.
    }
    if (followCtaTo && typeof window !== 'undefined') {
      // We don't have useNavigate here (the parent renders us conditionally),
      // so a plain href works fine — the SPA's router will pick it up on the
      // next nav handler. But the Link below already handles in-router nav.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfj-welcome-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        background: 'rgba(20, 32, 24, 0.72)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-base)',
        opacity: closing ? 0 : 1,
        transition: 'opacity 180ms ease',
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          color: 'var(--ink)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-3)',
          maxWidth: 480,
          width: '100%',
          padding: 'var(--space-xl) var(--space-lg) var(--space-lg)',
          position: 'relative',
        }}
      >
        {/* Top-right skip — always available, brand-quiet */}
        <button
          type="button"
          onClick={() => void finish()}
          aria-label="Skip welcome"
          style={{
            position: 'absolute',
            top: 12,
            right: 14,
            background: 'transparent',
            border: 'none',
            color: 'var(--muted)',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--small)',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          Skip
        </button>

        <div
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--micro)',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'var(--gold)',
            marginBottom: 'var(--space-sm)',
            fontWeight: 600,
          }}
        >
          {slide.eyebrow}
        </div>
        <h2
          id="mfj-welcome-title"
          style={{
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            fontSize: 'clamp(1.5rem, 6vw, 2rem)',
            lineHeight: 1.05,
            letterSpacing: '-0.5px',
            textTransform: 'uppercase',
            margin: 0,
            marginBottom: 'var(--space-sm)',
            color: 'var(--green)',
          }}
        >
          {slide.title}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 'var(--body)',
            lineHeight: 1.5,
            color: 'var(--ink)',
            opacity: 0.85,
          }}
        >
          {slide.body}
        </p>

        {/* Step dots — minimal, brand-coloured */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 'var(--space-lg)',
            marginBottom: 'var(--space-base)',
          }}
          aria-hidden
        >
          {SLIDES.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === index ? 24 : 8,
                height: 8,
                borderRadius: 4,
                background: i === index ? 'var(--green)' : 'var(--divider)',
                transition: 'width 220ms ease, background 220ms ease',
              }}
            />
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {isLast && slide.cta?.to ? (
            <Link to={slide.cta.to} style={{ textDecoration: 'none' }} onClick={() => void finish(slide.cta!.to)}>
              <Button label={slide.cta.label} />
            </Link>
          ) : (
            <Button label="Next" onClick={() => setIndex((i) => Math.min(i + 1, SLIDES.length - 1))} />
          )}
          {!isLast ? (
            <button
              type="button"
              onClick={() => setIndex((i) => Math.max(i - 1, 0))}
              disabled={index === 0}
              style={{
                background: 'transparent',
                border: 'none',
                color: index === 0 ? 'var(--divider)' : 'var(--muted)',
                cursor: index === 0 ? 'default' : 'pointer',
                fontFamily: 'inherit',
                fontSize: 'var(--small)',
                padding: '8px 4px',
              }}
            >
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void finish()}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--muted)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 'var(--small)',
                padding: '8px 4px',
              }}
            >
              I’ll explore first
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
